import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Plus, Pencil, Trash2, Upload, ExternalLink, Eye } from "lucide-react";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEMENTS = [
  { v: "all", l: "All pages" },
  { v: "homepage", l: "Homepage" },
  { v: "job_listing", l: "Job listing page" },
  { v: "job_details", l: "Job details page" },
];
const DEVICES = [
  { v: "both", l: "Desktop + Mobile" },
  { v: "desktop", l: "Desktop only" },
  { v: "mobile", l: "Mobile only" },
];
const PRIORITIES = [
  { v: "high", l: "High" },
  { v: "normal", l: "Normal" },
  { v: "low", l: "Low" },
];
const STATUS_BADGE = {
  active: { l: "Active", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  inactive: { l: "Inactive", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  scheduled: { l: "Scheduled", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  expired: { l: "Expired", cls: "bg-rose-100 text-rose-600 border-rose-200" },
};

const emptyForm = {
  name: "", description: "", image_url: "", link_url: "", button_text: "",
  placement: "all", device: "both", priority: "normal", status: "active",
  start_date: "", end_date: "",
};

export default function AdminSponsors() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [editing, setEditing] = useState(null); // sponsor object or "new"
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, cfg] = await Promise.all([
        axios.get(`${API}/admin/sponsors`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
      ]);
      setItems(s.data.items || []);
      setEnabled(cfg.data.sponsored_enabled !== false);
    } catch (e) {
      toast.error("Failed to load sponsors");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (s) => {
    setForm({
      name: s.name || "", description: s.description || "", image_url: s.image_url || "",
      link_url: s.link_url || "", button_text: s.button_text || "",
      placement: s.placement || "all", device: s.device || "both",
      priority: s.priority || "normal", status: s.status || "active",
      start_date: s.start_date ? s.start_date.slice(0, 16) : "",
      end_date: s.end_date ? s.end_date.slice(0, 16) : "",
    });
    setEditing(s);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Sponsor name is required"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/sponsors`, body, { headers });
        toast.success("Sponsor created");
      } else {
        await axios.patch(`${API}/admin/sponsors/${editing.id}`, body, { headers });
        toast.success("Sponsor updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor "${s.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/sponsors/${s.id}`, { headers });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleStatus = async (s) => {
    const next = s.status === "active" ? "inactive" : "active";
    try {
      await axios.patch(`${API}/admin/sponsors/${s.id}`, { ...s, status: next }, { headers });
      load();
    } catch { toast.error("Update failed"); }
  };

  const setMasterEnabled = async (checked) => {
    setEnabled(checked); // optimistic
    try {
      await axios.patch(`${API}/admin/settings`, { sponsored_enabled: checked }, { headers });
      toast.success(checked ? "Sponsored section enabled" : "Sponsored section disabled");
    } catch {
      setEnabled(!checked); // revert
      toast.error("Update failed");
    }
  };

  const uploadImage = async (file) => {
    if (!file || editing === "new") {
      if (editing === "new") toast.error("Save the sponsor first, then upload an image");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await axios.post(`${API}/admin/sponsors/${editing.id}/image`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, image_url: r.data.image_url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-sponsors-tab">
      {/* Header + master toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold text-slate-900">Sponsored Ads</h2>
          <p className="text-sm text-slate-500">Manage floating sponsor cards shown across the site.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Label htmlFor="sponsored-enabled" className="text-slate-700 cursor-pointer">Show sponsored section</Label>
            <Switch id="sponsored-enabled" checked={enabled} onCheckedChange={setMasterEnabled} data-testid="sponsored-master-toggle" />
          </div>
          <Button onClick={openNew} data-testid="sponsored-new-btn" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
            <Plus className="h-4 w-4 mr-1" /> New sponsor
          </Button>
        </div>
      </div>

      {/* List */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Sponsor</th>
                <th className="text-left px-4 py-3">Placement</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Impr.</th>
                <th className="text-right px-4 py-3">Clicks</th>
                <th className="text-right px-4 py-3">CTR</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">No sponsors yet. Click "New sponsor" to add one.</td></tr>
              ) : items.map(s => {
                const st = STATUS_BADGE[s.effective_status] || STATUS_BADGE.inactive;
                return (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`sponsor-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {s.image_url ? (
                          <img src={s.image_url.startsWith("http") ? s.image_url : `${process.env.REACT_APP_BACKEND_URL}${s.image_url}`} alt="" className="h-10 w-14 object-cover rounded border border-slate-200" />
                        ) : (
                          <div className="h-10 w-14 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-[10px]">no image</div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900">{s.name}</div>
                          {s.description && <div className="text-xs text-slate-500 line-clamp-1 max-w-[240px]">{s.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{PLACEMENTS.find(p => p.v === s.placement)?.l || s.placement}<div className="text-[11px] text-slate-400">{DEVICES.find(d => d.v === s.device)?.l}</div></td>
                    <td className="px-4 py-3 capitalize">{s.priority}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleStatus(s)} data-testid={`sponsor-toggle-${s.id}`} className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.l}</button>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.impressions}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.clicks}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{s.ctr}%</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {s.link_url && (
                          <a href={s.link_url} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Open link"><ExternalLink className="h-3.5 w-3.5" /></a>
                        )}
                        <button onClick={() => openEdit(s)} data-testid={`sponsor-edit-${s.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(s)} data-testid={`sponsor-delete-${s.id}`} className="p-1.5 hover:bg-rose-50 hover:text-rose-600 rounded text-slate-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Editor dialog */}
      <Dialog open={editing !== null} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New sponsor" : `Edit — ${editing?.name}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Sponsor name *</Label>
              <Input data-testid="sponsor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Cravatte Executive" />
            </div>
            <div className="md:col-span-2">
              <Label>Short description</Label>
              <Textarea data-testid="sponsor-form-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional — one or two lines shown under the image." />
            </div>
            <div className="md:col-span-2">
              <Label>Banner image</Label>
              {form.image_url && (
                <div className="mb-2">
                  <img src={form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="preview" className="w-full h-32 object-cover rounded border border-slate-200" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*" data-testid="sponsor-form-image" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={editing === "new" || uploading} />
                {uploading && <span className="text-xs text-slate-500">Uploading…</span>}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                {editing === "new" ? "Save the sponsor first, then upload the banner." : "PNG/JPG/WEBP · max 5MB · recommended 800×400."}
              </p>
            </div>
            <div>
              <Label>Link URL</Label>
              <Input data-testid="sponsor-form-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label>Button text</Label>
              <Input data-testid="sponsor-form-button" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} placeholder="Learn more" />
            </div>
            <div>
              <Label>Placement</Label>
              <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                <SelectTrigger data-testid="sponsor-form-placement"><SelectValue /></SelectTrigger>
                <SelectContent>{PLACEMENTS.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Device</Label>
              <Select value={form.device} onValueChange={(v) => setForm({ ...form, device: v })}>
                <SelectTrigger data-testid="sponsor-form-device"><SelectValue /></SelectTrigger>
                <SelectContent>{DEVICES.map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="sponsor-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="sponsor-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start date (optional)</Label>
              <Input type="datetime-local" data-testid="sponsor-form-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End date (optional)</Label>
              <Input type="datetime-local" data-testid="sponsor-form-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>

          {/* Preview */}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2"><Eye className="h-3.5 w-3.5" /> Preview</div>
            <div className="max-w-[320px] bg-white border border-slate-200 shadow-lg rounded-2xl overflow-hidden" data-testid="sponsor-preview">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 bg-slate-50/70">
                <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500">Sponsored</span>
              </div>
              {form.image_url && <img src={form.image_url.startsWith("http") ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="" className="w-full h-32 object-cover" />}
              <div className="p-3">
                <div className="font-display font-semibold text-slate-900 text-sm">{form.name || "Sponsor name"}</div>
                {form.description && <p className="text-xs text-slate-600 mt-1 leading-snug line-clamp-2">{form.description}</p>}
                {form.link_url && (
                  <span className="mt-2.5 inline-flex items-center rounded-full bg-[#1A8FE3] text-white text-xs font-medium px-4 py-1.5">{form.button_text || "Learn More"}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="sponsor-save" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
